import React from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import { localizeDateShort } from "utils/dateFormats";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import ExportShipmentFDExcel from "./ShipmentFulfillmentDocumentComponents/ExportShipmentFDExcel";

import { useLazyListShipmentFDQuery } from "store/services/shipmentFulfillmentDocumentApi";

import { BaseTabs } from "components/Shared/BaseTabs";

import { useTablePagination } from "hooks";

const ShipmentFulfillmentDocumentListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Shipment Document" }];

  const shipmentFDTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Document ID",
      selector: (row) => row?.documentId,
      cell: (row) => {
        const displayId = row?.documentId;
        const path = `/app/crm/collection-document/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {displayId}
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Type",
      selector: (row) => row?.type,
      cell: (row) => row?.type,
    },
    {
      name: "ShipmentID",
      selector: (row) => row?.shipment?.shipmentId || "N/A",
      cell: (row) => row?.shipment?.shipmentId || "N/A",
    },
    {
      name: "Issue Date",
      selector: (row) => localizeDateShort(row?.issueDate),
    },
    {
      name: "Expiration Date",
      selector: (row) => localizeDateShort(row?.expirationDate),
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => row?.status,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/collection-document/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Shipment Document"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/collection-document/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Shipment Document">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchShipmentFD,
    { data: shipmentFDRes, isLoading, isFetching, errors },
  ] = useLazyListShipmentFDQuery();

  React.useEffect(() => {
    fetchShipmentFD({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchShipmentFD, keyword, perPage]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={errors} />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={shipmentFDTabs}
          defaultValue={activeTab}
          actions={<ExportShipmentFDExcel />}
        />

        <TableFilter
          searchLabel="documentId"
          disabled={isLoading || isFetching}
        >
        </TableFilter>
        <AppTable
          columns={columns}
          data={shipmentFDRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentFDRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ShipmentFulfillmentDocumentListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/collection-document/create">
        <Button startIcon={<Add />} variant="contained" color="primary">
          Add Shipment Document
        </Button>
      </Link>
    </>
  );
};
